import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 },
  }),
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14 lg:mb-16"
        >
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]">01 — What we build</p>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl text-[#003366] mt-4">
              Services &<br />Fabrication
            </h2>
          </div>
          <p className="max-w-md text-slate-600 text-base leading-relaxed">
            Everything is cut, welded and finished in-house in Albany. If it's aluminium and it needs
            to survive salt water or corrugations — we build it.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.slug}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
            >              <Link
                to={`/services#${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group relative flex flex-col bg-white border border-[#007bff]/45 p-4 md:p-7 h-full min-h-[190px] md:min-h-[280px] overflow-hidden transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-[#007bff] hover:shadow-[0_24px_44px_-18px_rgba(0,51,102,0.3)]"
              >
                {/* top accent bar grows on hover */}
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-[3px] w-10 bg-[#007bff] transition-[width] duration-300 group-hover:w-full"
                />
                {/* ghost number */}
                <span
                  aria-hidden
                  className="absolute -top-1 right-3 md:top-2 md:right-5 font-display font-black text-5xl md:text-7xl leading-none text-slate-100 group-hover:text-blue-50 transition-colors duration-300 select-none"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="relative flex items-center gap-2">
                  <span className="w-10 h-10 md:w-12 md:h-12 bg-[#003366] text-white flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-[#007bff]">
                    <s.icon size={20} strokeWidth={1.75} />
                  </span>
                  {s.tag === "Most Popular" && (
                    <span className="font-mono text-[9px] md:text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 bg-[#007bff] text-white">
                      Most Popular
                    </span>
                  )}
                </div>
                <div className="relative mt-4 md:mt-6">
                  <h3 className="font-display font-black uppercase tracking-tight text-sm md:text-xl text-[#343a40] group-hover:text-[#003366] transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="hidden sm:block mt-2 text-sm leading-relaxed text-slate-600">
                    {s.desc}
                  </p>
                </div>
                <span className="relative mt-auto pt-4 md:pt-6 inline-flex items-center gap-1.5 font-mono text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] text-[#007bff]">
                  View service
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="mt-12 md:mt-16 flex flex-col items-center gap-4 text-center"
        >
          <p className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
            Every quote is free, no obligation
          </p>
          <Link
            to="/quote"
            data-testid="services-free-quote-cta"
            className="group inline-flex items-center gap-3 bg-[#343a40] text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
          >
            Free Quote
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
